import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import styled from '@emotion/styled';
import { Checkbox, Table } from 'antd';
import Legend from 'components/molecules/Legend';
import { colorConstants } from 'config/colorConstants';
import { getBestWeeksColumns } from 'utils/getRecommendationTableColumns';
import { useFetchBestWeeksData } from 'hooks/promoRecommendation/useFetchBestWeeksData';
import { FilterContextData } from 'contexts/FilterContext';
import { calculateContainerHeight, getConfig, parseAttributeType } from 'utils/helperMethods';
import { UserContext } from 'contexts/ConfigContext';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { getBestweeksExcelData } from 'parser/graphToExcelDownloadParser';
import { ConfigKeys } from 'utils/Enums';
import SelectWithInfiniteScroller from 'components/atoms/SelectWithInfiniteScroller';
import { CustomSelect } from 'components/pages/GrowthAnalysis';

const BestWeeksSection = () => {
    const {
        allDimAndAttributeTypeList,
        allDimAndAttributeType,
        idForRestOfComponent,
        setHideGlobalTimeFilterComponent,
    } = useContext(FilterContextData);
    const [bestWeeksFilterItems, setBestWeeksFilterItems] = useState([]);
    const [selectedFilterItem, setSelectedFilterItem] = useState<any>(null);
    const [openFilterDropdown, setOpenFilterDropdown] = useState(false);

    const { appConfig } = useContext(UserContext);
    const bestWeeksConfig = getConfig(ConfigKeys.BEST_WEEKS, appConfig);
    const dimensionConfig = bestWeeksConfig?.dimensionConfig;
    const rankingCriteriaOptions = bestWeeksConfig.rankingCriteriaOptions;
    // Show products w 52W sales
    const [hideGapInSales, setHideGapInSales] = useState(false);
    const [rankingCriteria, setRankingCriteria] = useState<string>(rankingCriteriaOptions[0].value);

    const {
        error: bestWeeksError,
        fetchBestWeeksData,
        data: bestWeeksData,
        count,
        loading: bestWeeksDataLoading,
    } = useFetchBestWeeksData();

    const offsetRef = useRef(0);
    const bestWeeksTabelRef = useRef<any>();

    // for price tracking we want to hide time period filter selection in the global filter
    useEffect(() => {
        setHideGlobalTimeFilterComponent(true);
        // resetting the state
        return () => {
            setHideGlobalTimeFilterComponent(false);
        };
    }, []);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions'] &&
            !selectedFilterItem
        ) {
            const data = parseAttributeType(allDimAndAttributeTypeList['dimentions'])?.filter(
                (item: any) => !dimensionConfig?.excludedDimensions.includes(item.dimension),
            );
            const dim1Attributes = allDimAndAttributeTypeList['dimentions'][0];
            if (data.length) {
                setBestWeeksFilterItems(data);
                setSelectedFilterItem(data[dim1Attributes.attribute.length - 1]); // select last level from dim1 attributes as default
                setSelectedFilterItem(
                    data.find((item: any) => item.label === dimensionConfig.defaultDimension) || data[0],
                );
            }
        }
    }, [allDimAndAttributeTypeList?.loading, allDimAndAttributeType]);

    useEffect(() => {
        if (
            allDimAndAttributeTypeList &&
            'loading' in allDimAndAttributeTypeList &&
            !allDimAndAttributeTypeList['loading'] &&
            allDimAndAttributeTypeList['dimentions']
        ) {
            fetchBestWeeksData({
                variables: {
                    dimension: selectedFilterItem?.dimension,
                    limit: bestWeeksConfig?.limit,
                    offset: 0,
                    attributeName: selectedFilterItem?.label?.replace(' ', '_'),
                    filters: JSON.stringify(idForRestOfComponent),
                    rankingCriteria,
                    hideGapInSales,
                },
            });
        }
    }, [
        selectedFilterItem,
        allDimAndAttributeTypeList?.loading,
        allDimAndAttributeType,
        idForRestOfComponent,
        hideGapInSales,
        rankingCriteria,
    ]);

    const memoisedBestWeeksColumns = useMemo(() => {
        return getBestWeeksColumns(
            bestWeeksData?.bestWeeksMapping,
            selectedFilterItem,
            setSelectedFilterItem,
            bestWeeksFilterItems,
            openFilterDropdown,
            setOpenFilterDropdown,
        );
    }, [bestWeeksData, selectedFilterItem, openFilterDropdown]);

    const infiniteScrollOnNext = (loading: boolean) => {
        if (!loading) {
            const newOffset = offsetRef.current + bestWeeksConfig.limit;
            {
                fetchBestWeeksData({
                    variables: {
                        dimension: selectedFilterItem?.dimension,
                        limit: bestWeeksConfig?.limit,
                        offset: newOffset,
                        attributeName: selectedFilterItem?.label?.replace(' ', '_'),
                        filters: JSON.stringify(idForRestOfComponent),
                        rankingCriteria,
                        hideGapInSales,
                    },
                });
            }
            offsetRef.current = newOffset;
        }
    };

    const commonLoading = bestWeeksDataLoading || allDimAndAttributeTypeList?.loading;
    const tableHeight = calculateContainerHeight(1, 64 + 16 * 3);

    const completeExcelData = useMemo(() => {
        return getBestweeksExcelData(selectedFilterItem?.label, bestWeeksData);
    }, [selectedFilterItem, bestWeeksData]);

    return (
        <div className="bg-white rounded-lg p-4">
            <div className="mb-4">
                <h2 className="text-xl font-semibold text-gray-800">Best Weeks</h2>
            </div>

            <div className="mt-4">
                <div className="mb-2 flex justify-between items-center">
                    {/* TODO  */}
                    {/* <p className="text-promoColour font-xs">Highlighted cells are promoted weeks</p> */}
                    <div className={`flex items-center ${bestWeeksDataLoading ? 'pointer-events-none' : ''}`}>
                        <Checkbox checked={hideGapInSales} onChange={() => setHideGapInSales((prev) => !prev)} />
                        <span style={{ marginLeft: 8 }}>Show products w 52W sales</span>
                    </div>
                    <div className="flex items-center gap-1">
                        <div className="mr-2">
                            <Legend
                                data={[
                                    {
                                        label: 'Highest Ranking',
                                        color: colorConstants.limeGreen,
                                        style: { width: 8, height: 8, borderRadius: 2 },
                                    },
                                    {
                                        label: 'Lowest Ranking',
                                        color: colorConstants.red,
                                        style: { width: 8, height: 8, borderRadius: 2 },
                                    },
                                ]}
                            />
                        </div>
                    </div>
                    <div className="flex justify-center items-center gap-2">
                        <div className="flex gap-2 justify-center items-center ml-2">
                            <span className="text-xs text-promoColour">Ranking Criteria</span>
                            <CustomSelect
                                showSearch={false}
                                filterOption={false}
                                placeholder={`Select Multiple...`}
                                maxTagCount="responsive"
                                className="dropdown-height !h-8 !w-48"
                                showArrow
                                onChange={(val: any) => {
                                    setRankingCriteria(val);
                                }}
                                value={rankingCriteria}
                                options={rankingCriteriaOptions}
                            />
                        </div>
                        <div className={`${commonLoading ? 'pointer-events-none' : ''}`}>
                            <DownloadExcel
                                title={'Best Weeks Data'}
                                excelData={[]}
                                headers={[]}
                                completeDataWithHeaders={completeExcelData}
                            />
                        </div>
                    </div>
                </div>

                <div className="flex flex-col">
                    <div id="best-weeks-table" ref={bestWeeksTabelRef}>
                        <InfiniteScroller
                            data={bestWeeksData?.weeksData}
                            loading={commonLoading}
                            hasMore={bestWeeksData?.weeksData?.length < count || commonLoading}
                            height={bestWeeksData?.weeksData && !bestWeeksError ? tableHeight : 'auto'}
                            onNext={() => infiniteScrollOnNext(commonLoading)}
                        >
                            <CustomTable
                                style={{
                                    opacity: commonLoading ? '0.3' : 1,
                                    pointerEvents: commonLoading ? 'none' : 'auto',
                                }}
                                pagination={false}
                                scroll={{ x: '100%' }}
                                sticky
                                columns={memoisedBestWeeksColumns}
                                dataSource={bestWeeksData?.weeksData || null}
                                // locale={{
                                //     emptyText: expandedTableDataError ? (
                                //         <ErrorBox msg={String(expandedTableDataError)} />
                                //     ) : (
                                //         ''
                                //     ),
                                // }}
                                bordered
                            />
                        </InfiniteScroller>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default BestWeeksSection;

const CustomTable = styled(Table)`
    .ant-table-thead > tr > th {
        background: ${colorConstants.grey};
        padding: 0px;
        height: 44px;
    }
    .ant-table-body {
        overflow-y: auto !important;
    }
    .ant-table-sticky-scroll {
        position: static;
    }
    .bold {
        font-weight: bold;
    }

    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }
    .ant-table-column-sorter {
        position: relative;
        left: -10px;
    }
    .ant-table-column-sorter-down.active,
    .ant-table-column-sorter-up.active {
        ${tw`text-purpleFont`};
    }
    ${tw`border-t`}
    border-bottom: unset;
    ${tw`border border-borderLinePurpuleAlt`}
`;
